import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Loader } from '@googlemaps/js-api-loader';

const { __environment__ } = window as any;
const googleMapsKey = __environment__.REACT_APP_GOOGLE_MAPS_KEY;

type DirectionsEndpoint = string | google.maps.LatLng;

interface GoogleMapsContextProps {
  core?: google.maps.CoreLibrary;
  routes?: google.maps.RoutesLibrary;
  places?: google.maps.PlacesLibrary;
  getDirections: (
    origin: DirectionsEndpoint,
    destination: DirectionsEndpoint,
  ) => Promise<google.maps.DirectionsResult | undefined>;
}

export const GoogleMapsContext = createContext<GoogleMapsContextProps>({
  core: undefined,
  routes: undefined,
  places: undefined,
  getDirections: async () => undefined,
});

export const GoogleMapsContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [core, setCore] = useState<google.maps.CoreLibrary>();
  const [places, setPlaces] = useState<google.maps.PlacesLibrary>();
  const [routes, setRoutes] = useState<google.maps.RoutesLibrary>();

  useEffect(() => {
    const loader = new Loader({
      apiKey: googleMapsKey,
      version: 'weekly',
      libraries: ['core', 'places', 'routes'],
    });

    loader.importLibrary('core').then(setCore);
    loader.importLibrary('places').then(setPlaces);
    loader.importLibrary('routes').then(setRoutes);
  }, []);

  const getDirections = useCallback(
    async (origin: DirectionsEndpoint, destination: DirectionsEndpoint) => {
      if (routes) {
        const service = new routes.DirectionsService();

        if (service) {
          try {
            const request: google.maps.DirectionsRequest = {
              origin,
              destination,
              travelMode: google.maps.TravelMode.DRIVING,
            };

            const res = await service.route(request);
            return res;
          } catch (error) {
            // Failed to fetch directions
          }
        }
      }
    },
    [routes],
  );

  return (
    <GoogleMapsContext.Provider value={{ core, routes, places, getDirections }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => useContext(GoogleMapsContext);
